import { IApiError } from '@northladder/utilities';

import { ApiClient } from '../../../api-client';
import { IApiResponse } from '../../../types';

import { formatTenantCountryList, formatUserDetails } from './transformers';
import {
  IAdminPasswordLoginPayload,
  IChangePasswordPayload,
  IChangePasswordResponse,
  ILoginPayload,
  ILoginResponseData,
  ILoginTypeOtpResponseData,
  ILoginTypePayload,
  ILoginTypeResponseData,
  ILogoutOptions,
  ILogoutPayload,
  ILogoutResponseData,
  INotificationHistoryParam,
  INotificationHistoryResponse,
  INotificationViewedParam,
  IPasswordPolicyResponse,
  IProfileUpdatePayload,
  IProfileUpdateResponse,
  IResetPasswordPayload,
  IResetPasswordResponse,
  IResetPasswordWithTokenPayload,
  IResetPasswordWithTokenResponse,
  ISendUserEmailLoginOPTPayload,
  ISendUserForgotPasswordOTPPayload,
  ISendUserForgotPasswordOTPResponse,
  ISendUserLoginOPTPayload,
  ISendUserLoginOPTResponse,
  ITenantCountryListResponse,
  ITriggerResetPasswordEmailPayload,
  ITriggerResetPasswordResponse,
  IUnReadNotificationResponse,
  IUpdateExpoTokenPayload,
  IUpdateExpoTokenResponse,
  IUpdateUserLanguagePayload,
  IValidateSSOPayload,
  IValidateUserEmailLoginOPTPayload,
  IValidateUserLoginOPTPayload,
  IValidateUserLoginOPTResponse,
  IVerifyOtpPayload,
  IVerifyOtpResponseData,
  IVerifyResetPasswordTokenParams,
  IVerifyResetPasswordTokenResponse,
} from './types';
import {
  adminPasswordLoginUrl,
  changePasswordUrl,
  fetchNotificationHistoryUrl,
  fetchPasswordPolicyUrl,
  fetchTenantCountryListUrl,
  fetchUnreadNotificationUrl,
  loginTypeOtpUrl,
  loginTypeUrl,
  loginUrl,
  logoutUrl,
  notificationViewedUrl,
  profileUpdateUrl,
  resetPasswordUrl,
  resetPasswordWithTokenUrl,
  sendEmailLoginOTPUrl,
  sendForgotPasswordOTPUrl,
  sendLoginOTPUrl,
  triggerResetPasswordEmailUrl,
  updateUserExpoNotificationTokenUrl,
  updateUserLanguageUrl,
  validateEmailLoginOTPUrl,
  validateLoginOTPUrl,
  validateSSOUrl,
  verifyOtpUrl,
  verifyResetPasswordTokenUrl,
} from './urls';

/**
 * This is provides the API client methods for all user authentication,
 * authorization and identity services like login, forgot password etc.
 *
 * NOTE: The `refreshToken` service has been moved out as a global to avoid
 * cyclic dependencies since its to be consumed within the base `ApiClient`.
 *
 */
export class UserAccountsAPIClient extends ApiClient {
  private static classInstance?: UserAccountsAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'IDENTITY_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new UserAccountsAPIClient();
    }

    return this.classInstance;
  };

  /**
   * This provides login authentication services. Mainly expects a username/email
   * & password payload and retrieves the tokens and user info from the backend.
   */
  public loginUser = async (payload: ILoginPayload) => {
    const response = await this.post<ILoginResponseData, ILoginPayload>(
      loginUrl(),
      payload,
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return formatUserDetails({
      ...response.data.data,
      credentials: {
        type: 'EMAIL_LOGIN',
        email: payload.email,
        password: payload.password,
      },
    });
  };

  /**
   * API call to checks login type of user based on provided email.
   */
  public checkLoginTypeUser = async (payload: ILoginTypePayload) => {
    const response = await this.post<ILoginTypeResponseData, ILoginTypePayload>(
      loginTypeUrl(),
      payload,
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to make when user wants to login using `OTP`.
   */
  public loginUserOTP = async (payload: ILoginTypePayload) => {
    const response = await this.post<
      ILoginTypeOtpResponseData,
      ILoginTypePayload
    >(loginTypeOtpUrl(), payload, { requiresAuth: false });

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to validate the OTP sent to the user's email.
   */
  public verifyEmailLoginOtp = async (payload: IVerifyOtpPayload) => {
    const response = await this.post<IVerifyOtpResponseData, ILoginTypePayload>(
      verifyOtpUrl(),
      payload,
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return formatUserDetails({
      ...response.data.data,
      credentials: {
        type: 'OTP_LOGIN',
        email: payload.email,
        otp: payload.otp,
      },
    });
  };

  /**
   * API call to validate the SSO user.
   */
  public validateSSOUser = async (payload: IValidateSSOPayload) => {
    const response = await this.post<ILoginResponseData, IValidateSSOPayload>(
      validateSSOUrl(),
      payload,
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return formatUserDetails({
      ...response.data.data,
      credentials: {
        type: 'SSO_LOGIN',
        identifier: payload.identifier,
      },
    });
  };

  /**
   * API call to logout the admin user.
   */
  public logoutAdminUser = async (payload: ILogoutPayload) => {
    const response = await this.post<ILogoutResponseData, ILogoutPayload>(
      logoutUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API to login the admin user using email-id and password.
   */
  public loginAdminUser = async (payload: IAdminPasswordLoginPayload) => {
    const response = await this.post<
      ILoginResponseData,
      IAdminPasswordLoginPayload
    >(adminPasswordLoginUrl(), payload, { requiresAuth: false });
    if (!response.success) throw response;

    return formatUserDetails({
      ...response.data.data,
      credentials: {
        type: 'EMAIL_LOGIN',
        email: payload.email,
        password: payload.password,
      },
    });
  };

  /**
   * This sends the user's phone number to which the OTP will be sent to start
   * the login process via phone number & OTP.
   */
  public sendUserLoginOTP = async (payload: ISendUserLoginOPTPayload) => {
    const response = await this.post<
      ISendUserLoginOPTResponse,
      ISendUserLoginOPTPayload
    >(sendLoginOTPUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * This sends the user's email to which the OTP will be sent to start
   * the login process via email & OTP.
   */
  public sendUserEmailLoginOTP = async (
    payload: ISendUserEmailLoginOPTPayload
  ) => {
    const response = await this.post<
      ISendUserLoginOPTResponse,
      ISendUserEmailLoginOPTPayload
    >(sendEmailLoginOTPUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * This sends the OTP to the server to be verified if it's what was sent on mobile
   * during the login process.
   */
  public validateUserLoginOTP = async (
    payload: IValidateUserLoginOPTPayload
  ) => {
    const response = await this.post<
      IValidateUserLoginOPTResponse,
      IValidateUserLoginOPTPayload
    >(validateLoginOTPUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return {
      ...response.data.data,
      credential: {
        type: 'PHONE_LOGIN',
        phoneNumber: payload.phoneNumber,
        // TODO: Generate this from keychain when payload.token is configured.
        token: '@TODO:GENERATE_THIS_FROM_KEY_CHAIN',
      },
    };
  };

  /**
   * This sends the OTP to the server to be verified if it's what was sent on mobile
   * during the login process.
   */
  public validateUserEmailLoginOTP = async (
    payload: IValidateUserEmailLoginOPTPayload
  ) => {
    const response = await this.post<
      IValidateUserLoginOPTResponse,
      IValidateUserEmailLoginOPTPayload
    >(validateEmailLoginOTPUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return {
      ...response.data.data,
      credential: {
        type: 'EMAIL_OTP_LOGIN',
        phoneNumber: '',
        // TODO: Generate this from secure storage when payload.token is configured.
        token: '@TODO:GENERATE_THIS_FROM_SE',
      },
    };
  };

  /**
   * This provides logout services.
   */
  public logoutUser = async (payloads: ILogoutOptions[]) => {
    const logoutPromises: Promise<
      IApiError | IApiResponse<ILogoutResponseData>
    >[] = [];

    payloads.forEach((payload: ILogoutOptions) => {
      const { refreshToken, identifier } = payload;

      const responsePromise = this.post<ILogoutResponseData, ILogoutPayload>(
        logoutUrl(),
        { refreshToken, identifier }
      );
      logoutPromises.push(responsePromise);
    });

    Promise.all(logoutPromises).then((logoutResponses) => {
      logoutResponses.forEach((response, index) => {
        const logoutResponse = { ...response, index };

        if (!logoutResponse.success) throw logoutResponse;
        return logoutResponse;
      });
    });
  };

  /**
   * This provides profile details update services.
   */
  public userProfileUpdate = async (payload: IProfileUpdatePayload) => {
    const response = await this.put<
      IProfileUpdateResponse,
      IProfileUpdatePayload
    >(profileUpdateUrl(), payload);

    if (!response.success) throw response;

    return response;
  };

  /**
   * This expects users email and sends the otp to that email address.
   */
  public sendUserForgotPasswordOTP = async (
    payload: ISendUserForgotPasswordOTPPayload
  ) => {
    const response = await this.post<
      ISendUserForgotPasswordOTPResponse,
      ISendUserForgotPasswordOTPPayload
    >(sendForgotPasswordOTPUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * This allows user to use OTP received in the email and change their password.
   */
  public resetPassword = async (payload: IResetPasswordPayload) => {
    const response = await this.post<
      IResetPasswordResponse,
      IResetPasswordPayload
    >(resetPasswordUrl(), payload, {
      requiresAuth: false,
    });

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * This allows user to change their password.
   */
  public changePassword = async (payload: IChangePasswordPayload) => {
    const response = await this.post<
      IChangePasswordResponse,
      IChangePasswordPayload
    >(changePasswordUrl(), payload);

    if (!response.success) throw response;

    return response;
  };

  /**
   * API call to fetch all notification history.
   */
  public fetchNotificationHistoryList = async (
    queryParams: INotificationHistoryParam
  ) => {
    const response = await this.get<INotificationHistoryResponse>(
      fetchNotificationHistoryUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  public updateNotificationViewedStatus = async (
    payload: INotificationViewedParam,
    markAll?: boolean
  ) => {
    const response = await this.put<INotificationViewedParam>(
      notificationViewedUrl(markAll),
      payload
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to fetch Unread notification status.
   */
  public fetchUnreadNotification = async () => {
    const response = await this.get<IUnReadNotificationResponse>(
      fetchUnreadNotificationUrl()
    );

    if (!response.success) throw response;

    return response.data?.data?.unReadNotification || false;
  };

  /**
   * Api call to update language of the user.
   */
  public updateUserLanguage = async (payload: IUpdateUserLanguagePayload) => {
    const response = await this.put<IUnReadNotificationResponse>(
      updateUserLanguageUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to update expo notification token on server.
   */
  public updateExpoNotificationToken = async (
    payload: IUpdateExpoTokenPayload | null
  ) => {
    if (!payload) return null;

    const response = await this.put<IUpdateExpoTokenResponse>(
      updateUserExpoNotificationTokenUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to fetch tenant country codes.
   */
  public fetchTenantCountryList = async (tenantId: string) => {
    const response = await this.get<ITenantCountryListResponse>(
      fetchTenantCountryListUrl(tenantId),
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return formatTenantCountryList(response.data?.data);
  };

  /**
   * Api call to trigger password reset email.
   */
  public triggerPasswordResetEmail = async (
    payload: ITriggerResetPasswordEmailPayload
  ) => {
    const response = await this.post<ITriggerResetPasswordResponse>(
      triggerResetPasswordEmailUrl(),
      payload,
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to validate reset token.
   */
  public validateResetToken = async (
    params: IVerifyResetPasswordTokenParams
  ) => {
    const response = await this.get<IVerifyResetPasswordTokenResponse>(
      verifyResetPasswordTokenUrl(params),
      { requiresAuth: false }
    );

    if (!response.success || !response.data?.data.sessionValid) throw response;

    return response.data?.data;
  };

  /**
   * Reset password with reset token
   */
  public resetPasswordWithToken = async (
    payload: IResetPasswordWithTokenPayload
  ) => {
    const response = await this.post<IResetPasswordWithTokenResponse>(
      resetPasswordWithTokenUrl(),
      payload,
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to fetch password policy list.
   */
  public fetchPasswordPolicyList = async () => {
    const response = await this.get<IPasswordPolicyResponse>(
      fetchPasswordPolicyUrl(),
      { requiresAuth: false }
    );

    if (!response.success) throw response;

    return response.data.data;
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for All User Accounts related requests.
 */
export const USER_ACCOUNTS_API = UserAccountsAPIClient.getClientInstance();
